import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useRealtimeSubscription } from '@dockethub/realtime'
import { getOrder, type OrderDto, type OrderItemDto } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { useActiveOrderStore } from '../stores/active-order.store.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { GuestActions } from '../components/GuestActions.js'
import { useStadiumMode } from '../lib/stadium.js'
import { StadiumTracker } from './StadiumTracker.js'

/** Estado visible de una ronda, derivado del estado de SUS ítems (el guest no
 *  recibe el estado de la ronda directo; lo inferimos). PENDING con ronda =
 *  sometida, esperando el visto bueno del mesero. */
function roundTone(items: OrderItemDto[]): { statusLabel: string; bg: string; fg: string } {
  const st = items.map((i) => i.status)
  if (st.some((s) => s === 'PENDING')) return { statusLabel: 'Por confirmar', bg: '#FEF3C7', fg: '#92400E' }
  if (st.length > 0 && st.every((s) => s === 'DELIVERED')) return { statusLabel: 'Entregado', bg: '#DCFCE7', fg: '#166534' }
  if (st.length > 0 && st.every((s) => s === 'READY' || s === 'DELIVERED')) return { statusLabel: 'Listo', bg: '#DBEAFE', fg: '#1E40AF' }
  return { statusLabel: 'En preparación', bg: '#E0E7FF', fg: '#3730A3' }
}

interface RoundGroup {
  key: string
  label: string
  items: OrderItemDto[]
  statusLabel: string
  tone: { bg: string; fg: string }
}

/** Agrupa los ítems por su ronda (roundId), en orden de aparición. Los ítems sin
 *  ronda (aún en el carrito, no sometidos) van juntos al final. Numera las rondas
 *  1, 2, 3… según el orden en que aparecen. */
function groupByRound(items: OrderItemDto[]): RoundGroup[] {
  const order: string[] = []
  const byKey = new Map<string, OrderItemDto[]>()
  for (const it of items) {
    const k = it.roundId ?? '__cart__'
    if (!byKey.has(k)) { byKey.set(k, []); order.push(k) }
    byKey.get(k)!.push(it)
  }
  let n = 0
  return order.map((k) => {
    const groupItems = byKey.get(k)!
    const tone = roundTone(groupItems)
    if (k === '__cart__') {
      return { key: k, label: 'En tu carrito', items: groupItems, statusLabel: 'Sin enviar', tone: { bg: '#F3F4F6', fg: '#6B7280' } }
    }
    n += 1
    return { key: k, label: `Ronda ${n}`, items: groupItems, statusLabel: tone.statusLabel, tone: { bg: tone.bg, fg: tone.fg } }
  })
}

/** Modo estadio + QR para llevar → seguimiento con pasos y código de entrega.
 *  Cualquier otro caso usa el seguimiento de siempre, sin cambios. */
export function OrderTracker() {
  const stadium = useStadiumMode()
  const orderMode = useSessionStore((s) => s.session?.orderMode)
  if (stadium && orderMode === 'TAKEOUT') return <StadiumTracker />
  return <DefaultOrderTracker />
}

function DefaultOrderTracker() {
  const { slug, orderId } = useParams<{ slug: string; orderId: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const [order, setOrder] = useState<OrderDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Refetch REST del pedido (fuente de verdad). Lo dispara TANTO el poll de
  // respaldo como el evento realtime del canal de la sesión.
  const token = session?.token
  const refetch = useCallback(async (): Promise<void> => {
    if (!token || !orderId) return
    const r = await getOrder(token, orderId)
    if (!r.ok) { setError(`${r.error.code}: ${r.error.message}`); return }
    setOrder(r.data)
    setError(null)
    // El pedido terminó (entregado o cancelado) → soltar SÓLO ese rastro
    // (los otros pedidos de la visita siguen en curso).
    if (r.data.deliveredAt || r.data.status === 'CANCELLED') {
      useActiveOrderStore.getState().removeByOrderId(r.data.id)
    }
  }, [token, orderId])

  useEffect(() => {
    if (!session) {
      navigate(`/r/${slug}`, { replace: true })
      return
    }
    if (!orderId) return
    void refetch()
    // Poll de RESPALDO (por si el WS se cae, o para el progreso de cocina que
    // no viaja por el canal de la sesión). El realtime hace la aceptación/rechazo/
    // disparo/entrega instantáneos vía useRealtimeSubscription (abajo).
    const interval = setInterval(() => void refetch(), 8000)
    return () => clearInterval(interval)
  }, [session, slug, orderId, navigate, refetch])

  // Tiempo real: cualquier evento de MI orden (aceptada/rechazada/a cocina/
  // entregada) llega al canal privado `ts:{tsid}:orders` → refrescamos de una.
  // El payload se ignora: el REST getOrder es la fuente de verdad.
  useRealtimeSubscription(
    session?.tableSessionId ? `ts:${session.tableSessionId}:orders` : '',
    () => { void refetch() },
    [session?.tableSessionId, refetch],
  )

  // Otros pedidos takeout de ESTA visita (para saltar entre ellos sin volver al
  // menú). Más nuevo primero; se excluye el que ya se está viendo.
  const siblingOrders = useActiveOrderStore((s) => s.orders)
    .filter((o) => o.orderId !== orderId && o.tableSessionId === session?.tableSessionId)
    .slice()
    .sort((a, b) => b.placedAt - a.placedAt)

  if (!session) return <></>

  // El estado que le importa al comensal es la PREPARACIÓN (cocina), no el estado
  // financiero. Una orden prepagada (online) queda CLOSED = PAGADA, pero la comida
  // puede seguir cocinándose → mostrar "En preparación/Listo" + un chip "Pagado",
  // NUNCA "Cerrado" (que sugiere que ya terminó).
  const isTakeout = order?.type === 'TAKEOUT'
  // Código de recolección SERVER-AUTHORITATIVE. Antes aquí se pintaba
  // `orderId.slice(0,8)`: un folio inventado en el cliente que el restaurante no
  // podía validar contra nada, así que al recoger no había forma de saber de
  // quién era el pedido. Si la orden aún no trae código (backend viejo), no
  // inventamos uno: no mostramos la tarjeta.
  const pickupCode = order?.pickupCode ?? null
  const trackingUrl = typeof window !== 'undefined' ? window.location.href : ''
  const paid = !!order && order.totalCents > 0 && (order.paidCents ?? 0) >= order.totalCents
  const liveItems = (order?.items ?? []).filter((it) => it.status !== 'CANCELLED')
  // Takeout OPEN sin pagar = espera pago en mostrador (cocina no lo recibió aún).
  const awaitingPayment = isTakeout && order?.status === 'OPEN' && !paid

  /** Fase de cocina derivada de los ítems (lo que el comensal ve como progreso). */
  function kitchenPhase(): 'preparing' | 'ready' | 'delivered' | null {
    if (liveItems.length === 0) return null
    if (liveItems.every((it) => it.status === 'DELIVERED')) return 'delivered'
    if (liveItems.every((it) => it.status === 'READY' || it.status === 'DELIVERED')) return 'ready'
    if (liveItems.some((it) => ['SENT', 'PREPARING', 'READY'].includes(it.status))) return 'preparing'
    return null
  }

  // Fase VISIBLE del comensal (prioridad: cancelada → sin aceptar → sin pago → cocina).
  let statusLabel = 'Cargando…'
  let statusCls = ''
  let heading = 'Tu pedido'
  if (order) {
    if (order.status === 'CANCELLED') {
      statusLabel = 'Cancelada'; statusCls = ''; heading = 'Tu pedido fue cancelado'
    } else if (order.status === 'REJECTED') {
      statusLabel = 'No aceptada'; statusCls = ''; heading = 'El restaurante no pudo aceptar tu pedido'
    } else if (awaitingPayment) {
      statusLabel = 'Esperando tu pago'; statusCls = 'preparing'
      heading = 'Tu pedido para llevar'
    } else if (order.status === 'PENDING_ACCEPTANCE') {
      statusLabel = 'Esperando confirmación'; statusCls = 'sent'
      heading = 'Esperando que el restaurante confirme'
    } else {
      const k = kitchenPhase()
      if (k === 'delivered') {
        statusLabel = 'Entregado'; statusCls = 'ready'; heading = '¡Gracias! Tu pedido fue entregado'
      } else if (k === 'ready') {
        statusLabel = isTakeout ? 'Listo para recoger' : 'Listo'; statusCls = 'ready'
        heading = isTakeout ? '¡Tu pedido está listo para recoger!' : 'Tu orden está lista'
      } else {
        statusLabel = 'En preparación'; statusCls = 'preparing'
        heading = isTakeout ? 'Estamos preparando tu pedido' : 'Preparando tu orden'
      }
    }
  }

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} customerName={session.customerName} branchId={session.branchId} />
      <div className="container">
        {error && (
          <div className="card" style={{ borderColor: 'var(--danger)' }}>
            <strong className="danger">Error al consultar tu orden</strong>
            <div className="muted">{error}</div>
          </div>
        )}
        {/* Código de recolección: es LO que el comensal tiene que conservar. Va
            arriba, grande, con el link de seguimiento a la vista para que pueda
            tomarle captura — si cierra la pestaña, sin esto no hay forma de
            volver a su pedido ni de reclamarlo en el mostrador. */}
        {pickupCode && isTakeout && !order?.deliveredAt && (
          <div className="card join-card">
            <div className="join-eyebrow">
              {awaitingPayment ? 'Tu pedido espera el pago' : 'Tu código de recolección'}
            </div>
            <div className="join-code" aria-label="Código de recolección">
              {pickupCode}
            </div>
            <p className="join-hint">
              {awaitingPayment
                ? 'Preséntate en el mostrador con este código para pagar. En cuanto el cajero confirme el pago, tu pedido entra a cocina.'
                : 'Enséñalo al recoger: con él confirmamos que el pedido es tuyo.'}
            </p>
            <p className="join-hint" style={{ marginTop: 8 }}>
              <strong>Toma una captura de esta pantalla.</strong> Si cierras esta
              página, puedes volver a seguir tu pedido aquí:
            </p>
            <div
              className="muted"
              style={{ wordBreak: 'break-all', fontSize: 13, marginTop: 4 }}
            >
              {trackingUrl}
            </div>
            <button
              type="button"
              className="btn btn-outline"
              style={{ marginTop: 10 }}
              onClick={() => {
                void navigator.clipboard?.writeText(trackingUrl)
              }}
            >
              Copiar liga de seguimiento
            </button>
          </div>
        )}
        {siblingOrders.length > 0 && (
          <div className="card">
            <strong>Tus otros pedidos de esta visita</strong>
            <div className="muted" style={{ marginTop: 2, marginBottom: 10 }}>
              Cada uno tiene su propio código de recolección.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {siblingOrders.map((o) => (
                <Link
                  key={o.orderId}
                  to={`/r/${slug}/order/${o.orderId}`}
                  className="row"
                  style={{
                    textDecoration: 'none', color: 'inherit', alignItems: 'center',
                    background: '#fff', border: '1px solid var(--line)', borderRadius: 12, padding: '10px 12px',
                  }}
                >
                  <div className="grow"><strong>{o.pickupCode ?? 'Ver pedido'}</strong></div>
                  <span aria-hidden="true" style={{ fontSize: 18 }}>→</span>
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="card">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={`status-pill ${statusCls}`}>{statusLabel}</span>
            {paid && (
              <span className="status-pill ready" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span aria-hidden>✓</span> Pagado
              </span>
            )}
          </div>
          <h2 style={{ marginTop: 12, marginBottom: 6 }}>{heading}</h2>
          <div className="muted">Se actualiza en vivo.</div>
          {order && (
            <div style={{ marginTop: 16 }}>
              {/* Agrupado por RONDA (tanda): el comensal pide en momentos
                  distintos y cada tanda tiene su propio estado (por confirmar /
                  en preparación / entregado). Los ítems sin ronda (recién
                  agregados, aún sin someter) caen en un grupo "en tu carrito". */}
              {groupByRound(liveItems).map((g) => (
                <div key={g.key} style={{ marginTop: 14 }}>
                  <div
                    className="row"
                    style={{ alignItems: 'center', marginBottom: 4 }}
                  >
                    <div className="grow" style={{ fontWeight: 700, fontSize: 13 }}>
                      {g.label}
                    </div>
                    <span
                      style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                        background: g.tone.bg, color: g.tone.fg,
                      }}
                    >
                      {g.statusLabel}
                    </span>
                  </div>
                  {g.items.map((it) => (
                    <div
                      key={it.id}
                      className="row"
                      style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}
                    >
                      <div className="grow">
                        <div style={{ fontWeight: 600 }}>
                          {(it.productName ?? it.name) ?? 'Producto'} × {it.quantity}
                        </div>
                        {it.modifiers && it.modifiers.length > 0 && (
                          <div className="muted" style={{ marginTop: 2 }}>
                            {it.modifiers.map((m) => m.name).join(' · ')}
                          </div>
                        )}
                        {it.notes && <div className="muted">{it.notes}</div>}
                      </div>
                      <div>
                        {formatMoney(
                          (it.unitPriceCents +
                            (it.modifiers ?? []).reduce((s, m) => s + m.priceDeltaCents, 0)) *
                            it.quantity,
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              <div
                className="row"
                style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 12 }}
              >
                <div className="grow"><strong>Total</strong></div>
                <div style={{ color: 'var(--accent-2)', fontWeight: 700 }}>
                  {formatMoney(order.totalCents)}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Sólo en MESA. Ahí el comensal está físicamente sentado con una cuenta
            abierta: pedir otra ronda suma a la MISMA cuenta (lo resuelve el
            carrito con /orders/by-session) y se paga una vez al final.
            Para llevar es otro proceso: el pedido queda cerrado al enviarse, con
            su código de recolección y su cobro. Ofrecer "agregar más" ahí creaba
            en silencio un SEGUNDO pedido con OTRO código — el comensal acababa
            con dos folios, dos cobros y sin ver el anterior. Si quiere más,
            arranca un pedido nuevo a conciencia desde el menú. */}
        {!isTakeout && (
          <Link to={`/r/${slug}/menu`} className="btn btn-outline" style={{ marginTop: 12 }}>
            + Agregar más a mi cuenta
          </Link>
        )}
        {/* OhSir pago de MESA (ítem D): el comensal del QR de mesa paga su
            cuenta desde la app. Sólo para DINE_IN — un pedido para llevar ya
            tiene su propio paso de pago. La cuenta no debe estar ya cerrada. */}
        {!isTakeout && order && order.status !== 'CLOSED' && order.status !== 'CANCELLED' && (
          <Link
            to={`/r/${slug}/cuenta`}
            className="btn btn-primary"
            style={{ marginTop: 10 }}
          >
            Pagar mi cuenta
          </Link>
        )}
      </div>
      <GuestActions showBill={!isTakeout && !paid} />
      <div className="footer">
        Powered by Nintai<em>GO</em> · en vivo
      </div>
    </div>
  )
}
